import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { setDoc, deleteDoc } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import { sortTemplates } from '../domain/noteTemplates';
import { messageTemplateDocument, messageTemplateUpdateDocument } from '../domain/compose';
import { useCollectionWindow } from './useCollectionWindow';

// New Message's saved templates (#597): MMS's composer `Templates ▾` — `Save
// New Template` and the saved ones — and the list Business Settings → Email &
// SMS → Custom Email Templates adds to, edits and deletes from (#608). The
// lesson notes' templates (useNoteTemplates.jsx) in shape, with a subject: a
// small managed list, read through the same window, in the same order (by
// name), with random ids for the same reason — two devices each saving
// "Welcome" offline saved two templates, and nothing points at one.

const MessageTemplatesContext = createContext(null);

// The note templates' bound: the picker is a flat menu.
const TRACK_LIMIT = 200;

export function MessageTemplatesProvider({ children }) {
  const { user } = useAuth();
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.messageTemplates,
    limit: TRACK_LIMIT,
  });
  const stored = read.docs;
  const loaded = read.hasRead;

  const value = useMemo(() => ({
    loaded,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: read.failed,
    templates: sortTemplates(stored),

    // Save New Template: the composer's subject and message under a name.
    // Null when the core would refuse it (no name, no message), so the
    // composer says why rather than the rules saying no.
    saveTemplate: async ({ label, subject, body }) => {
      if (!user) return null;
      const doc = messageTemplateDocument({ label, subject, body }, serverTimestamp());
      if (!doc) return null;
      const id = freeId('mtpl');
      // Merged, as a note template is: a new id, so there is nothing to
      // merge with, and no whole-document set for the queue to arbitrate.
      await setDoc(docRef(user.uid, COLLECTIONS.messageTemplates, id), doc, { merge: true });
      return id;
    },

    // Business Settings → Email & SMS → Custom Email Templates' edit (#608):
    // the fields that changed, merged onto the template, so an edit of its
    // subject on one device and of its message on another both land. Null
    // when the core would refuse it.
    updateTemplate: async (before, { label, subject, body }) => {
      if (!user || !before?.id) return null;
      const doc = messageTemplateUpdateDocument(before, { label, subject, body }, serverTimestamp());
      if (!doc) return null;
      await setDoc(docRef(user.uid, COLLECTIONS.messageTemplates, before.id), doc, { merge: true });
      return before.id;
    },

    removeTemplate: async (id) => {
      if (!user || !id) return;
      await deleteDoc(docRef(user.uid, COLLECTIONS.messageTemplates, id));
    },
  }), [stored, loaded, read.failed, user]);

  return <MessageTemplatesContext.Provider value={value}>{children}</MessageTemplatesContext.Provider>;
}

/**
 * The templates, or null where no provider is mounted — a composer rendered
 * on its own (a test) offers no Templates menu rather than a broken one, as
 * Insert Image offers no picker without a library (`useFilesIfProvided`).
 */
export function useMessageTemplatesIfProvided() {
  return useContext(MessageTemplatesContext);
}
